import { Suspense } from 'react';
import _ from 'lodash';
import {
    Icon,
    SuspenseBoundary,
    RepayDetailsHeader,
    RepayInfo,
    DisputeButton,
    FileList,
    LoaderOverlay
} from 'components';
import { 
    useDeductionDetails,
    usePaymentDocuments, 
    useRefreshDeductionDetails,
} from 'state';
import {
    PaymentDetail,
    RepayDetailHeaderData,
    RepayInfoData,
} from 'types';
import { faMoneyBillTransfer } from '@fortawesome/pro-regular-svg-icons'
import { useBackupDocument, useInvoiceParams } from 'hooks';
import DocumentErrorDialog from 'hooks/DocumentErrorDialog';
import styles from './RepayDetailsPage.module.scss';

type RepayDetailsProps = {
    repayDetails: PaymentDetail;
};

export default function RepayDetailsPage() {
    const { accountId, vendorId, invoiceId, voucherId, adjustmentIdentityKey } = useInvoiceParams();
    const repayDetails = useDeductionDetails(accountId, vendorId, invoiceId, undefined, voucherId, adjustmentIdentityKey);
    const refresh = useRefreshDeductionDetails(accountId, vendorId, invoiceId, undefined, voucherId, adjustmentIdentityKey);

    return (
        <SuspenseBoundary
            errorMessage={`Error retrieving repay details ${invoiceId}`}
            onReset={refresh}
        >
            <RepayDetailsComponent 
                repayDetails={repayDetails}
            />
        </SuspenseBoundary>
    );
}

const RepayDetailsComponent = ({ repayDetails }: RepayDetailsProps) => {
    const { vendorId } = useInvoiceParams();
    const getHeaderData = (): RepayDetailHeaderData => {
        return {
            type: repayDetails.transactionDescription ?? "Not Found",
            customerId: repayDetails.customerId ?? "Not Found",
            customerName: repayDetails.customerName ?? "Not Found",
            paymentIdRef: repayDetails.paymentId ?? "Not Found",
            repayNumber: repayDetails.invoiceId ?? "Not Found",
            vendorId: vendorId,
            vendorName: '',
        };
    }
    const repayDetailHeader: RepayDetailHeaderData = getHeaderData();

    const getRepayInfo = (): RepayInfoData => {
        return {
            invoiceDate: repayDetails.invoiceDate ? new Date(repayDetails.invoiceDate).toLocaleDateString() : "",
            repayAmount: repayDetails.adjustmentAmount,
            mockInvoiceNumber: repayDetails.mockInvoice ?? 'Not Found',
            repayNumber: repayDetails.invoiceId ?? 'Not Found',
            referenceNumber: repayDetails.customerItemReference ?? 'Not Found',
        }
    };
    const repayInfo: RepayInfoData = getRepayInfo();

    return (
        <div className="details-page">
            <div className='details-page-title'>
                <Icon icon={faMoneyBillTransfer}/> 
                Repay Details
            </div>
            <div>
                <div className={styles.buttonContainer}>
                    <DisputeButton 
                        selectedDetails={[repayDetails]} 
                        context="repayDetails"
                    />
                </div>
            </div>
            <div className="details-page-main">
                <div className={styles.header}>
                    <RepayDetailsHeader repayDetailHeader={repayDetailHeader}/>
                </div>
                <div className={styles.mainContent}>
                    <div className={styles.repayInfoContainer}>
                        <RepayInfo repayInfo={repayInfo}/>
                    </div>
                    <div className={styles.backupDocumentationContainer}>
                        <div className={styles.backupDocumentationTitle}>Backup Documentation</div>
                        <div className={styles.backupDocumentationFiles}>
                            <Suspense fallback={<LoaderOverlay isLoading />}>
                                {<Documents detail={repayDetails} />}
                            </Suspense>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

type DetailProps = {
    detail: PaymentDetail
};

const Documents = ({ detail }: DetailProps) => {
    const documents = usePaymentDocuments(detail);
    const getDocument = useBackupDocument();
    
    return _.isEmpty(documents)
        ? <span className={styles.none}>No documents found</span>
        : <>
            <FileList documents={documents} onClick={getDocument} />
            {getDocument.showError && <DocumentErrorDialog onClose={() => getDocument.setShowError(false)} documentIdentifier={getDocument.errorIdentifier} />}
          </>;
};
